import { APIClient } from "../helpers/api_helper";
import { ApiResponse } from "../types/api";

const api = new APIClient();

const RESOURCE = "/settings";

export interface TenantSettings {
  id?: number;
  tenant_id: number;
  booking_token?: string | null;
  locale: string;
  timezone: string;
  currency_code: string;
  require_service_recipe: boolean;
  prompt_consumption_on_checkout: boolean;
  use_catalog_categories: boolean;
  anamnesis_scope: "per_client" | "per_client_service";
  clinical_gate_mode: "warn" | "soft_block";
  clinical_record_retention_days: number | null;
  image_marketing_consent_expires_days: number | null;
  restrict_professional_services: boolean;
  block_expired_inventory?: boolean;
  use_commission_tracking: boolean;
  /** When true (and tracking ON), retail lines with professional freeze commission — GAP-F2-138. */
  commission_on_products: boolean;
  commission_calculation: {
    deduct_proportional_discount: boolean;
    deduct_gateway_fee_share: boolean;
    deduct_product_cogs: boolean;
  };
  max_manual_discount_bps: number;
  allow_receptionist_discount: boolean;
  courtesy_digest_email_enabled: boolean;
  courtesy_digest_include_managers: boolean;
  courtesy_highlight_bps: number;
  cash_register_mode: "unified" | "per_professional";
  day_close_reminder_hour: number | null;
  day_close_reminder_hour_resolved?: number;
  near_goal_reminder_hour: number | null;
  near_goal_reminder_hour_resolved?: number;
  package_suggested_discount_percent: number;
  show_professional_photos_in_online_booking: boolean;
  deposit_percent_options: number[];
  deposit_expires_hours: number;
  inactive_client_days: number;
  retention_enabled: boolean;
  return_reminders_enabled: boolean;
  return_reminder_lead_days: number;
  birthday_greetings_enabled: boolean;
  birthday_lead_days: number;
  reactivation_campaigns_enabled: boolean;
  nps_enabled: boolean;
  nps_delay_hours: number;
  nps_promoter_threshold: number;
  appointment_reviews_enabled: boolean;
  appointment_review_delay_hours: number;
  created_at?: string;
  updated_at?: string;
}

export const DEFAULT_TENANT_SETTINGS: TenantSettings = {
  tenant_id: 0,
  locale: "pt-BR",
  timezone: "America/Sao_Paulo",
  currency_code: "BRL",
  require_service_recipe: false,
  prompt_consumption_on_checkout: true,
  block_expired_inventory: false,
  use_catalog_categories: false,
  anamnesis_scope: "per_client",
  clinical_gate_mode: "warn",
  clinical_record_retention_days: null,
  image_marketing_consent_expires_days: null,
  restrict_professional_services: false,
  use_commission_tracking: true,
  commission_on_products: false,
  commission_calculation: {
    deduct_proportional_discount: true,
    deduct_gateway_fee_share: false,
    deduct_product_cogs: false,
  },
  max_manual_discount_bps: 2000,
  allow_receptionist_discount: false,
  courtesy_digest_email_enabled: true,
  courtesy_digest_include_managers: false,
  courtesy_highlight_bps: 1500,
  cash_register_mode: "unified",
  day_close_reminder_hour: null,
  near_goal_reminder_hour: null,
  package_suggested_discount_percent: 20,
  show_professional_photos_in_online_booking: true,
  deposit_percent_options: [1000, 2000, 5000],
  deposit_expires_hours: 24,
  inactive_client_days: 45,
  retention_enabled: true,
  return_reminders_enabled: true,
  return_reminder_lead_days: 0,
  birthday_greetings_enabled: false,
  birthday_lead_days: 0,
  reactivation_campaigns_enabled: true,
  nps_enabled: true,
  nps_delay_hours: 2,
  nps_promoter_threshold: 9,
  appointment_reviews_enabled: true,
  appointment_review_delay_hours: 2,
};

export const getSettings = (): Promise<ApiResponse<TenantSettings>> =>
  api.get<ApiResponse<TenantSettings>>(RESOURCE);

export interface UpdateTenantSettingsPayload {
  use_catalog_categories?: boolean;
  anamnesis_scope?: "per_client" | "per_client_service";
  clinical_gate_mode?: "warn" | "soft_block";
  clinical_record_retention_days?: number | null;
  restrict_professional_services?: boolean;
  use_commission_tracking?: boolean;
  commission_on_products?: boolean;
  commission_calculation?: {
    deduct_proportional_discount?: boolean;
    deduct_gateway_fee_share?: boolean;
    deduct_product_cogs?: boolean;
  };
  max_manual_discount_bps?: number;
  allow_receptionist_discount?: boolean;
  courtesy_digest_email_enabled?: boolean;
  courtesy_digest_include_managers?: boolean;
  courtesy_highlight_bps?: number;
  cash_register_mode?: "unified" | "per_professional";
  day_close_reminder_hour?: number | null;
  near_goal_reminder_hour?: number | null;
  package_suggested_discount_percent?: number;
  locale?: string;
  timezone?: string;
  currency_code?: string;
  appointment_reviews_enabled?: boolean;
  appointment_review_delay_hours?: number;
  image_marketing_consent_expires_days?: number | null;
  block_expired_inventory?: boolean;
}

export const updateSettings = (
  payload: UpdateTenantSettingsPayload
): Promise<ApiResponse<TenantSettings>> =>
  api.update<ApiResponse<TenantSettings>>(RESOURCE, payload);
